import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { lookupTicker, type TickerResult } from "@/lib/api";
import { IMG_BASE } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

const POPULAR_TICKERS = [
  { symbol: "AAPL", name: "Apple" },
  { symbol: "MSFT", name: "Microsoft" },
  { symbol: "GOOGL", name: "Alphabet" },
  { symbol: "AMZN", name: "Amazon" },
  { symbol: "TSLA", name: "Tesla" },
  { symbol: "META", name: "Meta" },
  { symbol: "NVDA", name: "NVIDIA" },
  { symbol: "JPM", name: "JPMorgan" },
  { symbol: "V", name: "Visa" },
  { symbol: "WMT", name: "Walmart" },
] as const;

interface TickerPageProps {
  serverData?: TickerResult | null;
}

export default function TickerPage({ serverData }: TickerPageProps) {
  const { symbol } = useParams<{ symbol: string }>();
  const [ticker, setTicker] = useState<TickerResult | null>(serverData ?? null);
  const [loading, setLoading] = useState(!serverData);

  const upperSymbol = symbol?.toUpperCase() || "";

  useEffect(() => {
    if (serverData) return;
    if (!symbol) return;
    setLoading(true);
    setTicker(null);
    lookupTicker(symbol)
      .then(setTicker)
      .catch(() => setTicker({ error: "Failed to load ticker data" } as TickerResult))
      .finally(() => setLoading(false));
  }, [symbol, serverData]);

  if (loading) {
    return (
      <div className="py-32 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Looking up {upperSymbol}...</p>
      </div>
    );
  }

  if (!ticker || ticker.error) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <AlertCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Ticker not found</h1>
          <p className="text-sm text-secondary-foreground mb-6">
            {ticker?.error || `We couldn't find data for ticker "${upperSymbol}".`}
          </p>
          <Button asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Tickers", to: "/" },
        { label: ticker.symbol || upperSymbol },
      ]}
    >
      {/* Hero */}
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0">
            <img
              src={ticker.logo || `${IMG_BASE}/ticker/${upperSymbol}?size=256`}
              alt={`${ticker.name} logo`}
              className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl object-contain bg-white border shadow-sm"
              onError={(e) => {
                (e.target as HTMLElement).style.opacity = "0.3";
              }}
            />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                {ticker.symbol}
              </h1>
              {ticker.exchange && <Badge variant="secondary">{ticker.exchange}</Badge>}
            </div>
            <p className="text-lg text-secondary-foreground mb-3">{ticker.name}</p>
            {ticker.domain && (
              <Link
                to={`/logo/${ticker.domain}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
              >
                View {ticker.domain} brand page
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Quick Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/ticker/${upperSymbol}" />`} />
      </section>

      {/* API Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- Ticker logo -->\n<img src="${IMG_BASE}/ticker/${upperSymbol}" />\n\n<!-- With size -->\n<img src="${IMG_BASE}/ticker/${upperSymbol}?size=128" />\n\n<!-- Ticker lookup API (JSON) -->\n<script>\nfetch("https://api.loadlogo.com/ticker/${upperSymbol}")\n</script>`} />
      </section>

      {/* Related Tickers */}
      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Popular Tickers</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {POPULAR_TICKERS.filter((t) => t.symbol !== upperSymbol).map((t) => (
            <Link
              key={t.symbol}
              to={`/ticker/${t.symbol}`}
              className="flex items-center gap-3 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <img
                src={`${IMG_BASE}/ticker/${t.symbol}?size=64`}
                alt={t.symbol}
                className="w-8 h-8 object-contain"
                loading="lazy"
              />
              <div className="min-w-0">
                <div className="text-sm font-bold">{t.symbol}</div>
                <div className="text-[11px] text-muted-foreground truncate">{t.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
